"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.CommsAgentServiceInstance = exports.CommsAgentService = exports.WhatsAppAgentServiceInstance = void 0;
const client_1 = require("@rws-framework/client");
const nest_interconnectors_1 = require("@rws-framework/nest-interconnectors");
/**
 * Unified agent service for both WhatsApp and Telegram.
 *
 * Backwards-compatible: still exported as `WhatsAppAgentService` / `WhatsAppAgentServiceInstance`
 * so existing imports keep working. Internally it tracks per-phone state for both channels.
 *
 * The WebSocket point used depends on the comms type:
 *   - WA   → `whatsapp_point`  (event: `whatsapp_agent_status`)
 *   - TELE → `telegram_point`  (event: `telegram_agent_status`)
 */
let WhatsAppAgentService = class WhatsAppAgentService extends client_1.RWSService {
    constructor(wsService) {
        super();
        this.wsService = wsService;
        /** @deprecated Use getPhoneState(phoneNumber) instead — this reflects the last-received phone only */
        this.agentActive = false;
        /** @deprecated Use getPhoneState(phoneNumber) instead */
        this.agentInitializing = false;
        this._phoneState = new Map();
        this._statusCallbacks = [];
        this._persistentListenerSetup = { wa: false, tele: false };
    }
    /** Update per-settings state, fire matching callbacks, and sync deprecated globals. */
    _updatePhoneState(settingsId, active, initializing, commsType = 'WA') {
        this._phoneState.set(settingsId, { active, initializing, commsType });
        // Keep legacy globals reflecting the most recent settings updated
        this.agentActive = active;
        this.agentInitializing = initializing;
        this._statusCallbacks
            .filter(cb => !cb.phoneNumber || cb.phoneNumber === settingsId)
            .forEach(cb => cb.fn(active, initializing));
    }
    /** Return per-settings state, or { active: false, initializing: false } if not yet known. */
    getPhoneState(settingsId) {
        var _a;
        return (_a = this._phoneState.get(settingsId)) !== null && _a !== void 0 ? _a : { active: false, initializing: false };
    }
    /** Return the comms type tracked for a settings id (defaults to 'WA'). */
    getPhoneCommsType(settingsId) {
        var _a, _b;
        return (_b = (_a = this._phoneState.get(settingsId)) === null || _a === void 0 ? void 0 : _a.commsType) !== null && _b !== void 0 ? _b : 'WA';
    }
    /**
     * Set up persistent WS listeners for both `whatsapp_agent_status` and `telegram_agent_status`.
     * Must be called once during application init or before first use.
     */
    setupPersistentListener() {
        if (!this._persistentListenerSetup.wa) {
            this._persistentListenerSetup.wa = true;
            this.wsService.listenForMessage('comms_point', (wsResponse) => {
                const { data } = wsResponse;
                if (!data || !data.settingsId)
                    return;
                const active = data.running || false;
                const initializing = data.initializing || false;
                this._updatePhoneState(String(data.settingsId), active, initializing, 'WA');
            }, 'comms_agent_status');
        }
        if (!this._persistentListenerSetup.tele) {
            this._persistentListenerSetup.tele = true;
            this.wsService.listenForMessage('telegram_point', (wsResponse) => {
                const { data } = wsResponse;
                if (!data || !data.settingsId)
                    return;
                const active = data.running || false;
                const initializing = data.initializing || false;
                this._updatePhoneState(String(data.settingsId), active, initializing, 'TELE');
            }, 'telegram_agent_status');
        }
    }
    /**
     * Send a status ping and wait up to 3 s for a response.
     * Updates `agentActive` and calls any registered callbacks.
     * Returns true if the agent responded as running, false on timeout.
     *
     * @param phoneNumber  Phone number / identifier of the agent
     * @param commsType    'WA' (default) or 'TELE' — selects which WS point to query
     */
    checkAgentStatus(settingsId, commsType = 'WA') {
        if (!settingsId)
            return Promise.resolve(false);
        this.setupPersistentListener();
        const pointName = commsType === 'TELE' ? 'telegram_point' : 'comms_point';
        const statusEvent = commsType === 'TELE' ? 'telegram_agent_status' : 'comms_agent_status';
        const statusMethod = commsType === 'TELE' ? 'telegram_status' : 'comms_status';
        return new Promise(async (resolve) => {
            const timer = setTimeout(() => {
                off();
                // Timeout — resolve with the last known state for this specific settings id.
                const known = this._phoneState.get(settingsId);
                resolve(known ? (known.active || known.initializing) : false);
            }, 3000);
            const off = this.wsService.listenForMessage(pointName, (wsResponse) => {
                var _a, _b, _c;
                if (String((_a = wsResponse.data) === null || _a === void 0 ? void 0 : _a.settingsId) !== String(settingsId))
                    return;
                clearTimeout(timer);
                off();
                const active = ((_b = wsResponse.data) === null || _b === void 0 ? void 0 : _b.running) || false;
                const initializing = ((_c = wsResponse.data) === null || _c === void 0 ? void 0 : _c.initializing) || false;
                this._updatePhoneState(settingsId, active, initializing, commsType);
                resolve(active || initializing);
            }, statusEvent, true);
            try {
                await this.wsService.waitForStatus();
            }
            catch (e) {
                console.warn('[WhatsAppAgentService] WS did not connect in time:', e);
                clearTimeout(timer);
                off();
                const known = this._phoneState.get(settingsId);
                resolve(known ? (known.active || known.initializing) : false);
                return;
            }
            this.wsService.sendMessage(pointName, statusMethod, {
                settingsId: settingsId.trim()
            });
        });
    }
    /**
     * Register a callback invoked whenever agent status changes for the given phoneNumber.
     * Returns an unsubscribe function.
     */
    onStatusChange(settingsId, callback) {
        const entry = { phoneNumber: settingsId, fn: callback };
        this._statusCallbacks.push(entry);
        return () => {
            this._statusCallbacks = this._statusCallbacks.filter(cb => cb !== entry);
        };
    }
    setAgentActive(active, settingsId, commsType = 'WA') {
        var _a, _b;
        if (settingsId) {
            const initializing = active ? ((_b = (_a = this._phoneState.get(settingsId)) === null || _a === void 0 ? void 0 : _a.initializing) !== null && _b !== void 0 ? _b : false) : false;
            this._updatePhoneState(settingsId, active, initializing, commsType);
        }
        else {
            // Legacy: update all known settings (fallback for callers that don't pass settingsId)
            this.agentActive = active;
            if (!active)
                this.agentInitializing = false;
            this._statusCallbacks.forEach(cb => cb.fn(active, this.agentInitializing));
        }
    }
};
exports.WhatsAppAgentServiceInstance = WhatsAppAgentService;
exports.CommsAgentService = WhatsAppAgentService;
exports.CommsAgentServiceInstance = WhatsAppAgentService;
exports.CommsAgentServiceInstance = exports.CommsAgentService = exports.WhatsAppAgentServiceInstance = WhatsAppAgentService = __decorate([
    __param(0, nest_interconnectors_1.WSService),
    __metadata("design:paramtypes", [typeof (_a = typeof nest_interconnectors_1.WSServiceInstance !== "undefined" && nest_interconnectors_1.WSServiceInstance) === "function" ? _a : Object])
], WhatsAppAgentService);
exports.default = WhatsAppAgentService.getSingleton();
